import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { Box, Collapse, Flex, FormLabel, Switch } from '@chakra-ui/react'
import type { FieldPath, FieldPathValue, FieldValues, UseFormRegisterReturn } from 'BrokerTsLib/src/rhf'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { forwardRef } from 'react'

/**
 * A form section (uses react-hook-form context) that can be toggled on and off with a switch.
 * @param name The path to field in the form context that determines if the section is visible.
 */
export const SwitchableFormSection = forwardRef(
  function SwitchableFormSection<
    TFieldValues extends FieldValues,
    TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,

  >(
    {
      sectionTitle,
      children,
      wide,
      register,
      ...rest
    }: {
      sectionTitle: React.ReactNode
      children: React.ReactNode
      register: UseFormRegisterReturn<TFieldName>
    } & FormSectionProps,
    ref: React.Ref<HTMLDivElement>) {
    const { watch, setValue } = useFormContext<FieldValues>()

    const showContent = watch(register.name)

    return (
      <FormSection
        ref={ref}
        cardHeaderProps={{
          onClick: () => setValue(register.name, !showContent as FieldPathValue<FieldValues, TFieldName>),
          numberBadgeProps: {
            background: showContent ? 'primary.500' : 'none',
            border: '1px solid',
            borderColor: 'primary.500',
            color: showContent ? 'white' : 'primary.500'
          }
        }}
        bodyProps={{ sx: { pb: 0 } }}
        sectionTitle={
          <Flex onClick={(e) => e.stopPropagation()} gap={4} alignItems='center' justifyContent='space-between'>
            <FormLabel flexGrow={1} fontSize='lg' fontWeight={'bold'} htmlFor={register.name}>{sectionTitle}</FormLabel>
            <Switch size='lg' id={register.name} {...register} isChecked={showContent} />
          </Flex>
        }
        wide={wide}
        {...rest}
      >
        <Collapse in={showContent} startingHeight={0}>
          <Box sx={{
            pb: { base: 8, md: wide ? 8 : 24 },
          }}>
            {children}
          </Box>
        </Collapse>
      </FormSection>
    )
  })